import React, { createContext, useState, useCallback, useContext, useMemo, useRef, useEffect } from 'react';
import { format } from 'date-fns';
import useDashboard from '../../hooks/useDashboard';

const DashboardContext = createContext({});

export const useDashboardContext = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboardContext deve ser usado dentro de um DashboardProvider');
  }
  return context;
};

export const DashboardProvider = ({ children }) => {
  const [dateStart, setDateStart] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [dateEnd, setDateEnd] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [queueId, setQueueId] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [filterVisible, setFilterVisible] = useState(false);
  const isMounted = useRef(true);

  const {
    findWithExtendedMetrics,
    preloadDashboardData,
    forceRefresh,
    invalidateCache,
    loading: dashboardLoading
  } = useDashboard();

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const apiParams = useMemo(() => {
    let params = {};

    if (dateStart) {
      params = {
        ...params,
        date_from: dateStart,
      };
    }

    if (dateEnd) {
      params = {
        ...params,
        date_to: dateEnd,
      };
    }

    if (queueId) {
      params = {
        ...params,
        queueIds: JSON.stringify([queueId]),
      };
    }

    return params;
  }, [dateStart, dateEnd, queueId]);

  const setDateRange = useCallback((start, end) => {
    if (isMounted.current) {
      setDateStart(start);
      setDateEnd(end);
    }
  }, []);

  const updateDateRange = setDateRange;

  const refresh = useCallback(async (forceReload = false) => {
    if (!isMounted.current) return null;

    setIsLoadingData(true);
    try {
      let result;

      if (forceReload) {
        result = await forceRefresh(apiParams);
      } else {
        result = await findWithExtendedMetrics(apiParams);
      }

      if (isMounted.current) {
        setRefreshKey(prev => prev + 1);
      }

      return result;
    } catch (error) {
      console.error('Erro ao atualizar dados do dashboard:', error);
      throw error;
    } finally {
      if (isMounted.current) {
        setIsLoadingData(false);
      }
    }
  }, [apiParams, findWithExtendedMetrics, forceRefresh]);

  const toggleFilterVisibility = useCallback(() => {
    if (isMounted.current) {
      setFilterVisible(prev => !prev);
    }
  }, []);

  const value = useMemo(() => ({
    dateStart,
    dateEnd,
    queueId,
    refreshKey,
    isLoading: isLoadingData || dashboardLoading,
    apiParams,
    setDateStart,
    setDateEnd,
    setQueueId,
    setDateRange,
    updateDateRange,
    refresh,
    invalidateCache,
    preloadDashboardData,
    filterVisible,
    toggleFilterVisibility
  }), [
    dateStart,
    dateEnd,
    queueId,
    refreshKey,
    isLoadingData,
    dashboardLoading,
    apiParams,
    setDateRange,
    refresh,
    invalidateCache,
    preloadDashboardData,
    filterVisible,
    toggleFilterVisibility,
    updateDateRange
  ]);

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
};

export default DashboardContext; 